import { format as formatDate } from 'date-fns/format';

import {
  DEFAULT_DATE_FORMATS,
  DEFAULT_MAX_DATE,
  DEFAULT_MIN_DATE,
} from '../constants/dateConstants';
import type { DatePickerMode, DatePickerValue } from '../types';

/**
 * Get the default date format based on the mode.
 */
export const getDefaultFormat = (mode: DatePickerMode): string => {
  return DEFAULT_DATE_FORMATS[mode] || DEFAULT_DATE_FORMATS.single;
};

/**
 * Get the default minimum date.
 */
export const getDefaultMinDate = (date?: Date) => date || DEFAULT_MIN_DATE;

/**
 * Get the default maximum date.
 */
export const getDefaultMaxDate = (date?: Date) => date || DEFAULT_MAX_DATE;

/**
 * Helper function to check if a value is a valid Date object.
 */
export const isValidDate = (date: unknown): date is Date => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * A value paired with its mode, so a branch on `mode` also narrows `value`.
 */
export type ModeValue =
  | { mode: 'range'; value: DatePickerValue<'range'> }
  | {
      mode: Exclude<DatePickerMode, 'range'>;
      value: DatePickerValue<'single'>;
    };

/**
 * Keeps only a value whose shape matches the mode: a `Date` for the single
 * modes, an object with `from`, `to` or both for range. Invalid dates are
 * dropped.
 */
export const sanitizeDate = (
  mode: DatePickerMode,
  date: unknown,
): ModeValue => {
  if (mode === 'range') {
    if (date && typeof date === 'object' && ('from' in date || 'to' in date)) {
      const from =
        'from' in date && isValidDate(date.from) ? date.from : undefined;
      const to = 'to' in date && isValidDate(date.to) ? date.to : undefined;
      // A range with neither end is no value, so it compares equal to the
      // undefined a cleared or rejected range commits.
      return { mode, value: from || to ? { from, to } : undefined };
    }
    return { mode, value: undefined };
  }
  return { mode, value: isValidDate(date) ? date : undefined };
};

const areDatesEqual = (left: Date | undefined, right: Date | undefined) =>
  left === right || (!!left && !!right && left.getTime() === right.getTime());

/**
 * Compares two values by the instants they hold, not by object identity.
 */
export const isValueEqual = (left: ModeValue, right: ModeValue): boolean => {
  if (left.value === undefined || right.value === undefined) {
    return left.value === right.value;
  }
  if (left.mode === 'range' && right.mode === 'range') {
    return (
      areDatesEqual(left.value.from, right.value?.from) &&
      areDatesEqual(left.value.to, right.value?.to)
    );
  }
  if (left.mode !== 'range' && right.mode !== 'range') {
    return areDatesEqual(left.value, right.value);
  }
  return false;
};

/**
 * Formats a value the way the input shows it and `onChange` reports it.
 * A half-open range keeps its separator (`"04/15/2024 - "`) so the typed
 * range parser can read it back.
 */
export const formatValue = (current: ModeValue, format: string): string => {
  if (current.mode === 'range') {
    const { from, to } = current.value ?? {};
    const fromText = isValidDate(from) ? formatDate(from, format) : '';
    const toText = isValidDate(to) ? formatDate(to, format) : '';
    return fromText || toText ? `${fromText} - ${toText}` : '';
  }
  return isValidDate(current.value) ? formatDate(current.value, format) : '';
};
